import { useEffect, useRef, useState } from 'react';

const valores = [
  {
    icon: 'ri-heart-pulse-line',
    title: 'Empatia Genuína',
    description:
      'Eu sei o que é sentir que ninguém entende. Por isso, cada detalhe deste projeto foi criado com o coração de quem já esteve no seu lugar.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Segurança e Acolhimento',
    description:
      'Aqui você encontra um espaço seguro, sem julgamentos. Sua história, suas dores e seus sonhos são tratados com o respeito que merecem.',
  },
  {
    icon: 'ri-brain-line',
    title: 'Ciência e Transformação',
    description:
      'Tudo o que oferecemos é baseado em neurociência, PNL e técnicas comprovadas. Nada de promessas vazias — apenas ferramentas reais que funcionam.',
  },
  {
    icon: 'ri-seedling-line',
    title: 'Liberdade como Propósito',
    description:
      'Meu maior desejo é que você conquiste a liberdade que eu levei 30 anos para encontrar. Que sua jornada seja mais curta, mais leve e mais bonita.',
  },
];

const ValoresSection = () => {
  const [visibleCards, setVisibleCards] = useState<Set<number>>(() => new Set());
  const [headerVisible, setHeaderVisible] = useState(false);
  const cardsRef = useRef<Map<number, HTMLDivElement>>(new Map());
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = Number(entry.target.getAttribute('data-idx'));
            if (!Number.isNaN(idx)) {
              setVisibleCards((prev) => {
                const next = new Set(prev);
                next.add(idx);
                return next;
              });
            }
            if (entry.target === headerRef.current) {
              setHeaderVisible(true);
            }
          }
        });
      },
      { threshold: 0.2 }
    );

    if (headerRef.current) observer.observe(headerRef.current);
    cardsRef.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section className="bg-luxury-cream py-24 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gold-500/20 to-transparent"></div>

      <div className="container mx-auto px-6 lg:px-16">
        <div ref={headerRef} className="text-center mb-16">
          {/* Badge - slide in */}
          <div
            className="flex items-center justify-center gap-4 mb-6 transition-all duration-700"
            style={{
              opacity: headerVisible ? 1 : 0,
              transform: headerVisible ? 'translateY(0)' : 'translateY(20px)',
            }}
          >
            <div
              className="h-px bg-gradient-to-r from-transparent to-gold-500/30 transition-all duration-700"
              style={{
                width: headerVisible ? '4rem' : '0rem',
                transitionDelay: '300ms',
              }}
            ></div>
            <span className="font-['Montserrat'] text-xs tracking-[0.3em] uppercase text-gold-600/60">
              O que nos move
            </span>
            <div
              className="h-px bg-gradient-to-l from-transparent to-gold-500/30 transition-all duration-700"
              style={{
                width: headerVisible ? '4rem' : '0rem',
                transitionDelay: '300ms',
              }}
            ></div>
          </div>
          {/* Title - fade up with blur */}
          <h2
            className="font-['Playfair_Display'] text-4xl lg:text-5xl text-luxury-charcoal font-light transition-all duration-800"
            style={{
              opacity: headerVisible ? 1 : 0,
              transform: headerVisible ? 'translateY(0)' : 'translateY(25px)',
              filter: headerVisible ? 'blur(0px)' : 'blur(3px)',
              transitionDelay: '200ms',
            }}
          >
            Nossos <span className="italic text-gold-600">valores</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {valores.map((valor, index) => {
            const isVisible = visibleCards.has(index);
            return (
              <div
                key={index}
                data-idx={index}
                ref={(el) => {
                  if (el) cardsRef.current.set(index, el);
                }}
                className="bg-white rounded-xl p-8 border border-gold-500/10 hover:border-gold-500/25 hover:shadow-lg hover:shadow-gold-500/5 group relative overflow-hidden"
                style={{
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.96)',
                  transition: `all 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${index * 120}ms`,
                }}
              >
                {/* Animated bottom line */}
                <div
                  className="absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 bg-gradient-to-r from-transparent via-gold-500/50 to-transparent rounded-full transition-all duration-700"
                  style={{
                    width: isVisible ? '60%' : '0%',
                    opacity: isVisible ? 1 : 0,
                    transitionDelay: `${index * 120 + 400}ms`,
                  }}
                ></div>

                <div
                  className="w-14 h-14 rounded-xl bg-gradient-to-br from-gold-500/10 to-gold-600/5 flex items-center justify-center mb-6 group-hover:scale-110 transition-all duration-500"
                  style={{
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? 'scale(1) rotate(0deg)' : 'scale(0.5) rotate(-10deg)',
                    transition: `all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) ${index * 120 + 200}ms`,
                  }}
                >
                  <i className={`${valor.icon} text-2xl text-gold-600`}></i>
                </div>
                <h3
                  className="font-['Playfair_Display'] text-xl text-luxury-charcoal mb-3 font-semibold transition-all duration-600"
                  style={{
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? 'translateX(0)' : 'translateX(-15px)',
                    transitionDelay: `${index * 120 + 300}ms`,
                  }}
                >
                  {valor.title}
                </h3>
                <p
                  className="font-['Montserrat'] text-xs text-luxury-charcoal/50 leading-relaxed font-light tracking-wide transition-all duration-600"
                  style={{
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? 'translateY(0)' : 'translateY(10px)',
                    transitionDelay: `${index * 120 + 400}ms`,
                  }}
                >
                  {valor.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ValoresSection;
